import { useState, useEffect, forwardRef } from "react";
import { Menu, X, LogIn } from "lucide-react";
import { useLocation } from "react-router-dom";
import logo from "@/assets/logo.png";

const Navbar = forwardRef<HTMLElement>((_, ref) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const showTransparent = isHome && !scrolled;

  return (
    <nav
      ref={ref}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        showTransparent
          ? "bg-transparent"
          : "bg-background/95 backdrop-blur-xl shadow-sm border-b border-border"
      }`}
    >
      <div className="container mx-auto flex items-center justify-between h-16 px-4">
        <a href="/">
          <img
            src={logo}
            alt="Index Rerum by EVOKE IT"
            className={`h-8 transition-all duration-500 ${showTransparent ? "brightness-0 invert" : ""}`}
          />
        </a>

        <div className="hidden md:flex items-center gap-8">
          {["Funcionalidades", "Para Quem", "FAQ", "Contacto"].map((item) => (
            <a
              key={item}
              href={isHome ? `#${item.toLowerCase().replace(/\s/g, "-")}` : `/#${item.toLowerCase().replace(/\s/g, "-")}`}
              className={`text-sm font-medium transition-colors duration-200 ${
                showTransparent
                  ? "text-white/70 hover:text-white"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {item}
            </a>
          ))}
          <a
            href="/login"
            className={`inline-flex items-center gap-1.5 text-sm font-medium transition-all duration-200 border-b pb-0.5 ${
              showTransparent
                ? "text-white border-white/30 hover:border-white"
                : "text-foreground border-foreground/30 hover:border-foreground"
            }`}
          >
            <LogIn size={15} />
            Iniciar sessão
          </a>
          <a
            href={isHome ? "#contacto" : "/#contacto"}
            className={`text-sm font-semibold px-5 py-2 transition-colors duration-200 ${
              showTransparent
                ? "bg-white text-foreground hover:bg-white/90"
                : "bg-foreground text-background hover:bg-foreground/85"
            }`}
          >
            Agendar Demo
          </a>
        </div>

        <button
          className={`md:hidden transition-colors ${showTransparent ? "text-white" : "text-foreground"}`}
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <>
          <div
            className="md:hidden fixed inset-0 top-16 bg-black/40 z-40"
            onClick={() => setOpen(false)}
          />
          <div className="md:hidden fixed left-0 right-0 top-16 bg-background px-4 pb-4 border-b border-border z-50">
            {["Funcionalidades", "Para Quem", "FAQ", "Contacto"].map((item) => (
              <a
                key={item}
                href={isHome ? `#${item.toLowerCase().replace(/\s/g, "-")}` : `/#${item.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => setOpen(false)}
                className="block py-3 text-muted-foreground hover:text-foreground transition-colors border-b border-border/50 font-medium"
              >
                {item}
              </a>
            ))}
            <a
              href="/login"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 py-3 text-foreground hover:text-foreground/70 transition-colors border-b border-border/50 font-medium"
            >
              <LogIn size={16} />
              Iniciar sessão
            </a>
            <a
              href={isHome ? "#contacto" : "/#contacto"}
              onClick={() => setOpen(false)}
              className="block mt-3 bg-foreground text-background text-center font-semibold px-5 py-2.5 hover:bg-foreground/85 transition-colors"
            >
              Agendar Demo
            </a>
          </div>
        </>
      )}
    </nav>
  );
});

Navbar.displayName = "Navbar";

export default Navbar;
